import { isKiosk } from './platform';
/*
 * Param customSearch can be used when we want to specify a stale or previous location state instead of the current global location object.
 * A common use case could be that we want to use the location object from react router after a redirect have taken place where the current location have been overwritten.
 */
export const getQuery = (customSearch) => {
    return new URLSearchParams(customSearch ? customSearch : window.location.search);
};
//This is used to tell the IKEA account service where to redirect after a successful login
//We are removing state and code query parameters when logging into IKEA account to avoid double code answers in url
export const stripURLOfQueryParameters = (params) => {
    const url = new URL(window.location.href);
    params.forEach(param => {
        if (url.searchParams.has(param)) {
            return url.searchParams.delete(param);
        }
    });
    return url.href;
};
export const getStoreId = () => {
    const storeIdCheck = (id) => {
        const regex = new RegExp('(\\d{3,5})');
        const foundStoreId = regex.exec(id);
        return foundStoreId && foundStoreId?.length > 0 ? foundStoreId[0] : null;
    };
    const storeIdFromQuery = getQuery().get('storeId');
    return storeIdFromQuery ? storeIdCheck(storeIdFromQuery) : null;
};
const rangeRegex = /\/(pax|besta|platsa|eket|smastad|ivar|bror|billy|ladmakare|kallax|boaxel|jonaxel|aurdal|elvarli|knoxhult|metod|lastare|trofast)\//;
export const getRange = () => {
    const query = getQuery();
    const range = query.get('range') || location.pathname.match(rangeRegex)?.[1];
    return (range ? range.toLowerCase() : 'pax');
};
export const getDefaultKvadratFeatures = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const spaceEnabled = searchParams.get('enableSpacePlanning') === 'true';
    const enableFullRoomEditor = rangeShouldEnableFullRoom() || searchParams.get('enableFullRoomEditor') === 'true';
    const enableInnerWallsAndObstacles = searchParams.get('enableInnerWallsAndObstacles') === 'true';
    const enableRemoveInnerWalls = searchParams.get('enableRemoveInnerWalls') === 'true';
    const enableRailings = searchParams.get('enableRailings') === 'true';
    const defaultFeatures = {
        editor2D: true,
        dragCorners: spaceEnabled || enableFullRoomEditor,
        splitWalls: spaceEnabled || enableFullRoomEditor,
        wallFeatures: enableInnerWallsAndObstacles || enableFullRoomEditor,
        deleteSurface: enableRemoveInnerWalls,
        obstacleConnectors: enableInnerWallsAndObstacles,
        obstacles: enableInnerWallsAndObstacles,
        railings: enableRailings,
        predefinedRooms: false,
        thickWalls: enableFullRoomEditor,
        slopedCeilings: enableFullRoomEditor,
        editingIn3D: enableFullRoomEditor,
        materialAndColors: enableFullRoomEditor,
        plumbing: false
    };
    return defaultFeatures;
};
const ALL_KVADRAT_FEATURES = {
    editor2D: 'editor2D',
    kioskMode: 'kioskMode',
    splitWalls: 'splitWalls',
    dragCorners: 'dragCorners',
    wallFeatures: 'wallFeatures',
    thickWalls: 'thickWalls',
    ambientLighting: 'ambientLighting',
    devMode: 'devMode',
    materialAndColors: 'materialAndColors',
    predefinedRooms: 'predefinedRooms',
    editingIn3D: 'editingIn3D',
    slopedCeilings: 'slopedCeilings',
    obstacles: 'obstacles',
    obstacleConnectors: 'obstacleConnectors',
    railings: 'railings',
    deleteSurface: 'deleteSurface',
    plumbing: 'plumbing'
};
export const getKvadratFeatures = () => {
    const enableKvadratFeatureFlags = getQuery().get('enableKvadratFeatureFlags');
    const enableKvadratFeatures = (enableKvadratFeatureFlags?.split(',') ?? []).reduce((acc, curr) => {
        if (!curr || !(curr in ALL_KVADRAT_FEATURES)) {
            return acc;
        }
        return { ...acc, [curr]: true };
    }, getDefaultKvadratFeatures());
    enableKvadratFeatures['kioskMode'] = isKiosk();
    return enableKvadratFeatures;
};
export const e2e = () => {
    return getQuery().get('e2e') === 'true';
};
export const genericMode = () => {
    return getQuery().get('generic') === 'true';
};
export const disableStyleInSelectedMenu = () => {
    return getQuery().get('disableStyleInSelectedMenu') === 'true';
};
export const rangeIsPax = () => {
    return getRange() === 'pax';
};
export const rangeIsPlatsa = () => {
    return getRange() === 'platsa';
};
export const rangeIsSmastad = () => {
    return getRange() === 'smastad';
};
export const rangeIsIvar = () => {
    return getRange() === 'ivar';
};
export const rangeIsBesta = () => {
    return getRange() === 'besta';
};
export const rangeIsBror = () => {
    return getRange() === 'bror';
};
export const rangeIsEket = () => {
    return getRange() === 'eket';
};
export const rangeIsBilly = () => {
    return getRange() === 'billy';
};
export const rangeIsKallax = () => {
    return getRange() === 'kallax';
};
export const rangeIsBoaxel = () => {
    return getRange() === 'boaxel';
};
export const rangeIsLadmakare = () => {
    return getRange() === 'ladmakare';
};
export const rangeIsElvarli = () => {
    return getRange() === 'elvarli';
};
export const rangeIsKnoxhult = () => {
    return getRange() === 'knoxhult';
};
export const rangeIsAurdal = () => {
    return getRange() === 'aurdal';
};
export const rangeIsMetod = () => {
    return getRange() === 'metod';
};
export const rangeIsTrofast = () => {
    return getRange() === 'trofast';
};
export const rangeIsKitchen = () => {
    return rangeIsKnoxhult() || rangeIsMetod();
};
export const rangeIsLastare = () => {
    return getRange() === 'lastare';
};
// Ranges where the full room editor is enabled by default (no enableFullRoomEditor query param
// needed). Add new ranges here when rolling out the full room editor to them.
export const rangeShouldEnableFullRoom = () => rangeIsPax() || rangeIsBesta() || rangeIsEket() || rangeIsMetod();
//# sourceMappingURL=queryUtils.js.map